import { Fragment, useRef, type ReactNode } from 'react';

export interface RadioRowOption {
  value: string;
  label: string;
  /** One line under the label. Carries the current selection or an explanation, never decoration. */
  hint?: string;
  /** A control on the right of the row — a "Choose" button that opens a picker. */
  action?: ReactNode;
  /**
   * A small preview between the radio and the label — the thing this option currently refers to.
   *
   * For a row whose meaning is "the image I picked", the file name alone makes you trust your memory;
   * the picture makes it checkable without reopening the picker.
   */
  visual?: ReactNode;
  /**
   * Content revealed directly beneath this row while it is the selected one.
   *
   * For settings that belong to ONE option — the generator and prompt for "generate an image", say.
   * Placing them after the whole group instead put a panel about the second option below the fourth,
   * which reads as belonging to whichever row it happens to sit under.
   */
  detail?: ReactNode;
}

/**
 * A set of mutually-exclusive rows.
 *
 * Buttons in a `radiogroup`, not `<input type="radio">`. This app has no native radio inputs anywhere,
 * for the same reason it has no native selects: the designed control is the house style, and mixing in
 * an OS-drawn radio next to designed rows reads as a bug. The ARIA roles are what make it a radio
 * group regardless — `radiogroup` + `radio` + `aria-checked` is exactly what a native group exposes,
 * so assistive tech sees no difference.
 *
 * Arrow keys move the selection, which is the part hand-rolled radios usually miss: inside a native
 * group the arrows select, and Tab leaves the group entirely.
 */
export function RadioRows({
  name,
  value,
  onChange,
  options,
}: {
  /** Distinguishes groups on the same screen, and names the group for assistive tech. */
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioRowOption[];
}) {
  const radios = useRef(new Map<string, HTMLButtonElement>());

  const move = (delta: number) => {
    const at = options.findIndex((o) => o.value === value);
    // Wraps, like a native group: from the last option, Down goes back to the first.
    const next = options[(at + delta + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    // Focus goes with the selection. Left on the old row, the next Space or Enter would press
    // that row and silently put the previous choice back.
    radios.current.get(next.value)?.focus();
  };

  return (
    <div role="radiogroup" aria-label={name} className="space-y-2">
      {options.map((option) => {
        const checked = option.value === value;
        const detailId = option.detail ? `${name}-${option.value}-detail` : undefined;
        return (
          <Fragment key={option.value}>
          <div
            className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${
              checked ? 'border-action bg-tint' : 'border-line bg-card hover:bg-hover'
            }`}
          >
            <button
              ref={(el) => {
                if (el) radios.current.set(option.value, el);
                else radios.current.delete(option.value);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              // Only the selected row is tabbable, so Tab enters the group once and leaves it once —
              // the roving-tabindex pattern a native radio group implements for free.
              tabIndex={checked ? 0 : -1}
              {...(detailId ? { 'aria-controls': detailId, 'aria-expanded': checked } : {})}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(1);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(-1);
                }
              }}
              className="focus-ring flex min-w-0 flex-1 items-start gap-2.5 text-left"
            >
              <span
                aria-hidden
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                  checked ? 'border-action' : 'border-line-input'
                }`}
              >
                {checked && <span className="h-2 w-2 rounded-full bg-action" />}
              </span>
              {option.visual && <span className="shrink-0">{option.visual}</span>}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-ink">{option.label}</span>
                {option.hint && (
                  <span className="block truncate text-xs text-ink-muted">{option.hint}</span>
                )}
              </span>
            </button>
            {option.action && <div className="shrink-0">{option.action}</div>}
          </div>
          {/*
            A conditional reveal, wired the way a native one is: the row owns `aria-controls` and
            `aria-expanded`, so the relationship is announced rather than merely visual. Rendering it
            INSIDE the group is deliberate and is what the pattern requires — the panel has to follow
            the row it belongs to, and a group whose panel sits after the last row says nothing about
            which option it configures.
          */}
          {option.detail && checked && (
            <div id={detailId} className="rounded-xl border border-line p-2.5">
              {option.detail}
            </div>
          )}
          </Fragment>
        );
      })}
    </div>
  );
}
